export type AuditAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'ARCHIVE'
  | 'RESTORE'
  | 'STATUS_CHANGE'
  | 'ASSIGNMENT'
  | 'PRIORITY_CHANGE'
  | 'LOGIN'
  | 'LOGOUT'
  | 'PASSWORD_CHANGE'
  | 'FAILED_LOGIN'
  | 'EXPORT'
  | 'CONFIG_CHANGE'
  | 'ROLE_CHANGE';

export type AuditEntityType =
  | 'Complaint'
  | 'WorkOrder'
  | 'User'
  | 'Vendor'
  | 'Technician'
  | 'Department'
  | 'Category'
  | 'Priority'
  | 'Status'
  | 'SLARule'
  | 'Config'
  | 'System';

export interface AuditLog {
  log_id: string;
  timestamp: string;
  user_id: string;
  user_name?: string;
  ip_address: string;
  user_agent: string;
  action: AuditAction;
  entity_type: AuditEntityType;
  entity_id: string;
  old_values?: Record<string, any>;
  new_values?: Record<string, any>;
  metadata?: Record<string, any>;
  description?: string;
}

export interface AuditLogFilter {
  action?: AuditAction | '';
  entity_type?: AuditEntityType | '';
  user_id?: string;
  date_from?: string;
  date_to?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedAuditLogs {
  logs: AuditLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface SecuritySettings {
  password_min_length: number;
  password_require_uppercase: boolean;
  password_require_numbers: boolean;
  password_require_special: boolean;
  session_timeout_minutes: number;
  max_login_attempts: number;
  lockout_duration_minutes: number;
  access_token_expiry_minutes: number;
  refresh_token_expiry_days: number;
  enforce_password_history: number;
  require_2fa: boolean;
}

export interface UpdateSecuritySettingsDto {
  password_min_length?: number;
  password_require_uppercase?: boolean;
  password_require_numbers?: boolean;
  password_require_special?: boolean;
  session_timeout_minutes?: number;
  max_login_attempts?: number;
  lockout_duration_minutes?: number;
  access_token_expiry_minutes?: number;
  refresh_token_expiry_days?: number;
  enforce_password_history?: number;
  require_2fa?: boolean;
}

export interface ActiveSession {
  session_id: string;
  user_id: string;
  user_name: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  last_active: string;
  is_current: boolean;
}

export interface LoginHistoryEntry {
  log_id: string;
  user_id: string;
  user_name: string;
  email: string;
  ip_address: string;
  user_agent: string;
  status: 'SUCCESS' | 'FAILED';
  failure_reason?: string;
  timestamp: string;
}

export const AUDIT_ACTION_LABELS: Record<AuditAction, string> = {
  CREATE: 'Created',
  UPDATE: 'Updated',
  DELETE: 'Deleted',
  ARCHIVE: 'Archived',
  RESTORE: 'Restored',
  STATUS_CHANGE: 'Status Changed',
  ASSIGNMENT: 'Assigned',
  PRIORITY_CHANGE: 'Priority Changed',
  LOGIN: 'Login',
  LOGOUT: 'Logout',
  PASSWORD_CHANGE: 'Password Changed',
  FAILED_LOGIN: 'Failed Login',
  EXPORT: 'Exported',
  CONFIG_CHANGE: 'Config Changed',
  ROLE_CHANGE: 'Role Changed',
};

export const AUDIT_ENTITY_LABELS: Record<AuditEntityType, string> = {
  Complaint: 'Complaint',
  WorkOrder: 'Work Order',
  User: 'User',
  Vendor: 'Vendor',
  Technician: 'Technician',
  Department: 'Department',
  Category: 'Category',
  Priority: 'Priority',
  Status: 'Status',
  SLARule: 'SLA Rule',
  Config: 'Configuration',
  System: 'System',
};

export const AUDIT_ACTION_COLORS: Record<AuditAction, string> = {
  CREATE: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  UPDATE: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  DELETE: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  ARCHIVE: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  RESTORE: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  STATUS_CHANGE: 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  ASSIGNMENT: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  PRIORITY_CHANGE: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  LOGIN: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  LOGOUT: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  PASSWORD_CHANGE: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  FAILED_LOGIN: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  EXPORT: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  CONFIG_CHANGE: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
  ROLE_CHANGE: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
};
